import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Star, Globe, Shield, Trash2, ExternalLink, ChevronLeft, CheckCircle, Users } from "lucide-react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "@/i18n";

const API = '/api';

const getLocalUserId = () => {
  try {
    let id = localStorage.getItem("havn_user_id");
    if (!id) {
      id = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem("havn_user_id", id);
    }
    return id;
  } catch {
    return `user_${Date.now()}`;
  }
};

const getFavicon = (domain) =>
  `https://www.google.com/s2/favicons?domain=${domain}&sz=32`;

const CATEGORY_ORDER = ["government", "banking", "health", "shopping", "news", "utilities", "shipping"];
const CATEGORY_LABELS = {
  government: "Government",
  banking:    "Banking",
  health:     "Health",
  shopping:   "Shopping",
  news:       "News",
  utilities:  "Utilities",
  shipping:   "Shipping",
};

// Site Card Component
function SiteCard({ domain, name, description, tier, onOpen, onRemove, note }) {
  // Note: tier labels are now translated at render time in the parent
  const tierConfig = {
    caregiver:  { color: "#f59e0b", label: "Caregiver pick",   dot: "*" },
    havn:   { color: "var(--havn-primary)", label: "HAVN Verified", dot: "Verified" },
    favorite:   { color: "#a78bfa", label: "Your favorite",    dot: "Saved" },
  };
  const t = tierConfig[tier] || tierConfig.havn;

  return (
    <motion.div
      className="ts-card"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid={`ts-card-${domain}`}
    >
      <div className="ts-card-favicon">
        <img
          src={getFavicon(domain)}
          alt=""
          onError={(e) => { e.target.style.display = "none"; }}
          width={24}
          height={24}
        />
      </div>
      <div className="ts-card-info">
        <span className="ts-card-name">{name || domain}</span>
        {description && <span className="ts-card-desc">{description}</span>}
        {note && <span className="ts-card-note">{note}</span>}
        <span className="ts-card-tier" style={{ color: t.color }}>
          {t.dot} {t.label}
        </span>
      </div>
      <div className="ts-card-actions">
        <button
          className="ts-open-btn"
          onClick={() => onOpen(domain, name)}
          aria-label={`Open ${name || domain}`}
          data-testid={`ts-open-${domain}`}
        >
          <ExternalLink size={16} />
        </button>
        {onRemove && (
          <button
            className="ts-remove-btn"
            onClick={() => onRemove(domain)}
            aria-label={`Remove ${name || domain}`}
            data-testid={`ts-remove-${domain}`}
          >
            <Trash2 size={14} />
          </button>
        )}
      </div>
    </motion.div>
  );
}

// Main Page
export default function TrustedSitesPage() {
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const { t } = useTranslation();
  const getUserId = useCallback(() => authUser?.id || getLocalUserId(), [authUser?.id]);
  const [activeTab, setActiveTab] = useState("favorites");  // favorites | all | caregiver
  const [curatedSites, setCuratedSites] = useState([]);
  const [caregiverSites, setCaregiverSites] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [openingDomain, setOpeningDomain] = useState(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    const userId = getUserId();
    try {
      const [curatedRes, favsRes] = await Promise.all([
        axios.get(`${API}/trusted-sites/curated`),
        axios.get(`${API}/favorites/${userId}`)
      ]);
      setCuratedSites(curatedRes.data?.sites || []);
      setFavorites(favsRes.data || []);

      // Load caregiver whitelist if access code exists
      try {
        const codeRes = await axios.get(`${API}/caregiver/access-code/${userId}`);
        const code = codeRes.data?.code;
        if (code) {
          const siteListRes = await axios.get(`${API}/caregiver/site-list/${userId}?code=${code}`);
          const whitelist = (siteListRes.data || []).filter(s => s.list_type === "whitelist");
          setCaregiverSites(whitelist);
        }
      } catch (_) {
        // No caregiver set — ok
      }
    } catch (err) {
      console.error("[TrustedSites] Load error", err);
    } finally {
      setIsLoading(false);
    }
  }, [getUserId]);

  useEffect(() => { load(); }, [load]);

  const handleOpen = useCallback(async (domain, name) => {
    setOpeningDomain(domain);
    // Speak before opening
    try {
      const text = `Opening ${name || domain} — this is a trusted site.`;
      const ttsRes = await axios.post(`${API}/speak`, { text, voice: "nova" }, { responseType: "blob", timeout: 8000 });
      if (ttsRes.data) {
        const url = URL.createObjectURL(new Blob([ttsRes.data], { type: "audio/mpeg" }));
        const audio = new Audio(url);
        audio.onended = () => {
          URL.revokeObjectURL(url);
          window.open(`https://${domain}`, "_blank", "noopener,noreferrer");
          setOpeningDomain(null);
        };
        audio.play().catch(() => {
          window.open(`https://${domain}`, "_blank", "noopener,noreferrer");
          setOpeningDomain(null);
        });
        return;
      }
    } catch (_) {}
    window.open(`https://${domain}`, "_blank", "noopener,noreferrer");
    setOpeningDomain(null);
  }, []);

  const handleRemoveFavorite = useCallback(async (domain) => {
    const userId = getUserId();
    try {
      await axios.delete(`${API}/favorites/${userId}/${encodeURIComponent(domain)}`);
      setFavorites(prev => prev.filter(f => f.domain !== domain));
    } catch (err) {
      console.error("[TrustedSites] Remove favorite error", err);
    }
  }, []);

  // Group curated sites by category
  const grouped = CATEGORY_ORDER.reduce((acc, cat) => {
    const sites = curatedSites.filter(s => s.category === cat);
    if (sites.length) acc[cat] = sites;
    return acc;
  }, {});

  // Filter logic
  const showCaregiver = activeTab === "all" || activeTab === "caregiver";
  const showCurated   = activeTab === "all";
  const showFavorites = activeTab === "all" || activeTab === "favorites";

  return (
    <div className="ts-page">
      {/* Header */}
      <header className="ts-header">
        <button className="ts-back-btn" onClick={() => navigate("/home")} aria-label={t('trusted_back')} data-testid="ts-back-btn">
          <ChevronLeft size={20} />
        </button>
        <div className="ts-header-center">
          <Shield size={18} className="ts-header-icon" />
          <h1 className="ts-header-title">{t('trusted_sites')}</h1>
        </div>
        <div style={{ width: 40 }} />
      </header>

      {/* Tabs */}
      <nav className="ts-tabs">
        {[
          { id: "all",       label: t('tab_all') },
          { id: "caregiver", label: t('tab_caregiver') },
          { id: "favorites", label: t('tab_my_saves') },
        ].map(({ id, label }) => (
          <button
            key={id}
            className={`ts-tab ${activeTab === id ? "active" : ""}`}
            onClick={() => setActiveTab(id)}
            data-testid={`ts-tab-${id}`}
          >
            {label}
          </button>
        ))}
      </nav>

      {isLoading && <div className="ts-loading">{t('trusted_loading')}</div>}

      <div className="ts-content">
        {/* Caregiver picks */}
        {showCaregiver && caregiverSites.length > 0 && (
          <section className="ts-section">
            <h2 className="ts-section-title">{t('trusted_caregiver_section')}</h2>
            {caregiverSites.map(s => (
              <SiteCard
                key={s.id}
                domain={s.domain}
                name={s.domain}
                note={s.note}
                tier="caregiver"
                onOpen={handleOpen}
              />
            ))}
          </section>
        )}

        {/* User favorites */}
        {showFavorites && (
          <section className="ts-section">
            <h2 className="ts-section-title">{t('trusted_saves_section')}</h2>
            {favorites.length === 0 ? (
              <p className="ts-empty">
                {t('trusted_no_saves')}
              </p>
            ) : (
              favorites.map(f => (
                <SiteCard
                  key={f.id}
                  domain={f.domain}
                  name={f.display_name || f.domain}
                  tier="favorite"
                  onOpen={handleOpen}
                  onRemove={handleRemoveFavorite}
                />
              ))
            )}
          </section>
        )}

        {/* HAVN curated — grouped by category */}
        {showCurated && Object.entries(grouped).map(([cat, sites]) => (
          <section key={cat} className="ts-section">
            <h2 className="ts-section-title">{CATEGORY_LABELS[cat] || cat}</h2>
            {sites.map(s => (
              <SiteCard
                key={s.domain}
                domain={s.domain}
                name={s.name}
                description={s.description}
                tier="havn"
                onOpen={handleOpen}
              />
            ))}
          </section>
        ))}
      </div>

      {/* Opening overlay */}
      <AnimatePresence>
        {openingDomain && (
          <motion.div className="ts-opening-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <CheckCircle size={32} style={{ color: "#22c55e" }} />
            <p>Opening {openingDomain}...</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
